import type { ReactNode } from 'react'
import { Icon } from '../../components/icons/Icon'
import { Popover } from './Popover'

export interface ActiveFilterChip {
  id: string
  label: string
}

export interface FilterPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  activeCount: number
  activeChips: ActiveFilterChip[]
  onRemoveChip: (id: string) => void
  onClear: () => void
  children: ReactNode
}

/**
 * Advanced filter builder shell (design-system.md §3.2 FilterBuilder): the
 * entity-specific facet fields are supplied by the caller as `children`;
 * this component owns the disclosure chrome, the active-filter chip
 * summary, and apply/clear affordances.
 */
export function FilterPanel({
  open,
  onOpenChange,
  activeCount,
  activeChips,
  onRemoveChip,
  onClear,
  children,
}: FilterPanelProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', alignItems: 'center' }}>
        <Popover
          label="Filters"
          open={open}
          onOpenChange={onOpenChange}
          width={340}
          badge={activeCount || undefined}
          triggerContent={
            <>
              <Icon name="filter" size={14} /> Filter
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
            {children}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
              <button type="button" className="iw-btn iw-btn--sm" onClick={onClear}>
                Clear
              </button>
              <button
                type="button"
                className="iw-btn iw-btn--primary iw-btn--sm"
                onClick={() => onOpenChange(false)}
              >
                Apply
              </button>
            </div>
          </div>
        </Popover>
      </div>
      {activeChips.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} aria-label="Active filters">
          {activeChips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              className="iw-chip"
              aria-pressed="true"
              onClick={() => onRemoveChip(chip.id)}
              aria-label={`Remove filter ${chip.label}`}
            >
              {chip.label} <Icon name="x" size={11} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
